import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../../services/apiService';
import { 
  Image, 
  FileText, 
  Star, 
  Mail, 
  Users, 
  Eye,
  Clock,
  TrendingUp,
  MessageSquare
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState({
    portfolio_count: 0,
    blog_count: 0,
    reviews_count: 0,
    contacts_unread: 0,
    bookings_pending: 0,
    newsletter_subscribers: 0
  });
  const [recentActivity, setRecentActivity] = useState({
    recent_contacts: [],
    recent_bookings: [],
    recent_posts: [],
    recent_projects: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Add timeout to prevent infinite hanging
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      const statsPromise = dashboardAPI.getStats();
      const activityPromise = dashboardAPI.getRecentActivity();
      
      const [statsResponse, activityResponse] = await Promise.race([
        Promise.all([statsPromise, activityPromise]),
        timeout
      ]);
      
      setStats(statsResponse.data || {
        portfolio_count: 0,
        blog_count: 0,
        reviews_count: 0,
        contacts_unread: 0,
        bookings_pending: 0,
        newsletter_subscribers: 0
      });
      
      setRecentActivity(activityResponse.data || {
        recent_contacts: [],
        recent_bookings: [],
        recent_posts: [],
        recent_projects: []
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default empty data on error
      setStats({
        portfolio_count: 0,
        blog_count: 0,
        reviews_count: 0,
        contacts_unread: 0,
        bookings_pending: 0,
        newsletter_subscribers: 0
      });
      setRecentActivity({
        recent_contacts: [],
        recent_bookings: [],
        recent_posts: [],
        recent_projects: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Проекты портфолио',
      value: stats.portfolio_count,
      icon: Image,
      color: 'bg-blue-500',
      link: '/admin/portfolio'
    },
    {
      title: 'Статьи блога',
      value: stats.blog_count,
      icon: FileText,
      color: 'bg-green-500',
      link: '/admin/blog'
    },
    {
      title: 'Отзывы',
      value: stats.reviews_count,
      icon: Star,
      color: 'bg-yellow-500',
      link: '/admin/reviews'
    },
    {
      title: 'Непрочитанные сообщения',
      value: stats.contacts_unread,
      icon: Mail,
      color: 'bg-red-500',
      link: '/admin/forms'
    },
    {
      title: 'Ожидающие брони',
      value: stats.bookings_pending,
      icon: Clock,
      color: 'bg-purple-500',
      link: '/admin/forms'
    },
    {
      title: 'Подписчики рассылки',
      value: stats.newsletter_subscribers,
      icon: Users,
      color: 'bg-indigo-500',
      link: '/admin/newsletter'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Панель управления</h1>
        <div className="text-sm text-gray-300">
          Добро пожаловать! Вот что происходит с вашим сайтом.
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Link
              key={index}
              to={card.link}
              className="bg-gray-800 overflow-hidden shadow-lg rounded-lg hover:shadow-xl transition-shadow duration-200 border border-gray-700"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${card.color} rounded-md p-3`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-300 truncate">
                        {card.title}
                      </dt>
                      <dd className="text-3xl font-semibold text-white">
                        {card.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Contacts */}
        <div className="bg-gray-800 shadow-lg rounded-lg border border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Последние контакты</h3>
              <Link 
                to="/admin/forms"
                className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
              >
                Посмотреть все
              </Link>
            </div>
            <div className="space-y-3">
              {recentActivity.recent_contacts.length > 0 ? (
                recentActivity.recent_contacts.map((contact, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-white">{contact.name}</p>
                        <p className="text-xs text-gray-300">{contact.email}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-300 text-sm">Нет последних контактов</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-gray-800 shadow-lg rounded-lg border border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Последние брони</h3>
              <Link 
                to="/admin/forms"
                className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
              >
                Посмотреть все
              </Link>
            </div>
            <div className="space-y-3">
              {recentActivity.recent_bookings.length > 0 ? (
                recentActivity.recent_bookings.map((booking, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-white">{booking.name}</p>
                        <p className="text-xs text-gray-300">{booking.service} - {booking.date}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      booking.status === 'pending' 
                        ? 'bg-yellow-100 text-yellow-800'
                        : booking.status === 'confirmed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-300 text-sm">Нет последних броней</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Blog Posts */}
        <div className="bg-gray-800 shadow-lg rounded-lg border border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Последние статьи</h3>
              <Link 
                to="/admin/blog"
                className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
              >
                Посмотреть все
              </Link>
            </div>
            <div className="space-y-3">
              {recentActivity.recent_posts.length > 0 ? (
                recentActivity.recent_posts.map((post, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-700 rounded-md">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{post.title_de}</p>
                      <p className="text-xs text-gray-300">
                        {new Date(post.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      post.published 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-300 text-sm">Нет последних статей</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Portfolio Projects */}
        <div className="bg-gray-800 shadow-lg rounded-lg border border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Последние проекты</h3>
              <Link 
                to="/admin/portfolio"
                className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
              >
                Посмотреть все
              </Link>
            </div>
            <div className="space-y-3">
              {recentActivity.recent_projects.length > 0 ? (
                recentActivity.recent_projects.map((project, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-700 rounded-md">
                    <Image className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{project.title_de}</p>
                      <p className="text-xs text-gray-300 capitalize">
                        {project.category} • {new Date(project.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {project.featured && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-300 text-sm">Нет последних проектов</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 shadow-lg rounded-lg border border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-4">Быстрые действия</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/portfolio"
              className="flex items-center p-4 bg-blue-900 bg-opacity-50 rounded-lg hover:bg-blue-800 hover:bg-opacity-70 transition-colors duration-200 border border-blue-700"
            >
              <Image className="h-8 w-8 text-blue-400 mr-3" />
              <span className="text-sm font-medium text-blue-200">Добавить проект</span>
            </Link>
            <Link
              to="/admin/blog"
              className="flex items-center p-4 bg-green-900 bg-opacity-50 rounded-lg hover:bg-green-800 hover:bg-opacity-70 transition-colors duration-200 border border-green-700"
            >
              <FileText className="h-8 w-8 text-green-400 mr-3" />
              <span className="text-sm font-medium text-green-200">Написать статью</span>
            </Link>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors duration-200 border border-gray-600"
            >
              <TrendingUp className="h-8 w-8 text-gray-400 mr-3" />
              <span className="text-sm font-medium text-gray-200">Посмотреть сайт</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;